import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Video Carousel Component
 * Displays latest 5 videos in horizontal scroll
 */
const VideoCarousel = ({ videos = [], theme, isMobile }) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll availability
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [videos]);

  // Scroll handlers
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = isMobile ? 280 : 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter and sort videos (exclude shorts under 60 seconds)
  const filteredVideos = videos
    .filter(v => !v.duration || v.duration > 60)
    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
    .slice(0, 5);

  if (!filteredVideos || filteredVideos.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '48px', position: 'relative' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: '600',
          color: theme?.colors?.primary || '#0f172a'
        }}>
          Latest Videos
        </h2>
        <Link
          to="/videos"
          style={{
            color: theme?.colors?.secondary || '#3b82f6',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          View All →
        </Link>
      </div>

      {/* Carousel Container */}
      <div style={{ position: 'relative' }}>
        {/* Left scroll button */}
        {!isMobile && canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            ←
          </button>
        )}

        {/* Scrollable container */}
        <div
          ref={scrollContainerRef}
          onScroll={checkScrollButtons}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: '8px',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {filteredVideos.map(video => (
            <Link
              key={video.id}
              to={`/video/${video.id}`}
              style={{
                minWidth: isMobile ? '280px' : '380px',
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                {/* Thumbnail */}
                <div style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                  backgroundColor: theme?.colors?.lightGray || '#f1f5f9',
                  backgroundImage: video.thumbnail ? `url(${video.thumbnail})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>
                  {/* Duration badge */}
                  {video.duration && (
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {formatDuration(video.duration)}
                    </div>
                  )}

                  {/* Play button overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}>
                    <div style={{
                      width: 0,
                      height: 0,
                      borderLeft: '16px solid white',
                      borderTop: '10px solid transparent',
                      borderBottom: '10px solid transparent',
                      marginLeft: '4px'
                    }} />
                  </div>
                </div>

                {/* Content */}
                <div style={{ padding: '16px' }}>
                  <h3 style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: theme?.colors?.cardText || '#1f2937',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4'
                  }}>
                    {video.title}
                  </h3>

                  {video.speaker && (
                    <p style={{
                      fontSize: '13px',
                      color: theme?.colors?.cardTextSecondary || '#6b7280',
                      marginBottom: '8px'
                    }}>
                      {video.speaker}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: theme?.colors?.textTertiary || '#9ca3af'
                  }}>
                    {video.views && (
                      <span>{video.views.toLocaleString()} views</span>
                    )}
                    <span>
                      {new Date(video.date || video.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Right scroll button */}
        {!isMobile && canScrollRight && (
          <button
            onClick={() => scroll('right')}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            →
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoCarousel;